import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { I18nextProvider } from 'react-i18next';

import App from './App';
import '@/styles/globals.css';
import '@/styles/mobile.css';
import '@/i18n/russian-fonts.css';
import i18n from '@/lib/i18n';

// Import performance utilities
import { NetworkOptimizer } from '@/lib/performance';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (renamed from cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Professional error boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Nova Cafe Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 mb-4">
              {i18n.t('common.somethingWentWrong', 'Что-то пошло не так')}
            </h1>
            <p className="text-neutral-600 mb-6">
              {i18n.t('common.sorryInconvenience', 'Извините за неудобства. Пожалуйста, обновите страницу.')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
            >
              {i18n.t('common.refreshPage', 'Обновить страницу')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}


// Initialize performance optimizations
function initializePerformance() {
  // Enable resource optimization
  NetworkOptimizer.enableResourceHints();
  
  // Prefetch likely pages based on user role (removed manifest.json as it's already in HTML)
  const userRole = localStorage.getItem('user-role');
  if (userRole === 'waiter') {
    NetworkOptimizer.prefetchResource('/pos', 'fetch');
  } else if (userRole === 'chef') {
    NetworkOptimizer.prefetchResource('/kitchen', 'fetch');
  }
}

// Initialize app
// registerSW(); // Temporarily disabled to debug Super Admin login issues
initializePerformance();

// Render app
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          {/* FIXED: Added future flags to remove warnings */}
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#0a0a0a',
                color: '#ffffff',
                borderRadius: '4px',
                padding: '16px',
                fontSize: '14px',
                fontWeight: '500',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#f9fafb',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#f9fafb',
                },
              },
            }}
          />
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  </React.StrictMode>
);